/**
 * Lógica pura del sistema de monedas (Vitalis / Pulse / Karma) y del
 * SmartScore consolidado.
 *
 * IMPORTANTE: la fuente de verdad de los saldos y de los topes es el backend.
 * Estas utilidades existen para render, formato y pruebas unitarias; nunca
 * para calcular saldos autoritativos en el cliente.
 */

export type PointDimension = 'vitalis' | 'pulse' | 'karma';

export interface DimensionMeta {
  key: PointDimension;
  label: string;
  emoji: string;
  color: string;
  tagline: string;
}

/** Orden de presentación de las monedas en la UI. */
export const DIMENSION_ORDER: PointDimension[] = ['vitalis', 'pulse', 'karma'];

export const DIMENSIONS: Record<PointDimension, DimensionMeta> = {
  vitalis: {
    key: 'vitalis',
    label: 'Vitalis',
    emoji: '🌿',
    color: '#A6FF00',
    tagline: 'Ergonomía y salud física'
  },
  pulse: {
    key: 'pulse',
    label: 'Pulse',
    emoji: '⚡',
    color: '#00F0FF',
    tagline: 'Agilidad mental y minijuegos'
  },
  karma: {
    key: 'karma',
    label: 'Karma',
    emoji: '💜',
    color: '#C084FC',
    tagline: 'Reconocimiento entre pares'
  }
};

export const SMART_SCORE_META = {
  label: 'SmartScore',
  emoji: '⭐',
  color: '#FFFFFF'
} as const;

/** Etiquetas de valor sugeridas al regalar Karma. */
export const KARMA_TAGS = ['Trabajo en equipo', 'Compañero de pausa', 'Empatía'] as const;

export interface SmartScoreValues {
  vitalis: number;
  pulse: number;
  karma: number;
}

function toSafePoints(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** SmartScore ⭐ = Vitalis 🌿 + Pulse ⚡ + Karma 💜 */
export function computeSmartScore(values: SmartScoreValues): number {
  return toSafePoints(values.vitalis) + toSafePoints(values.pulse) + toSafePoints(values.karma);
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
}

/** Puntos que aún se pueden ganar hoy en una dimensión con tope diario. */
export function remainingDailyCap(earnedToday: unknown, cap: unknown): number {
  return Math.max(Math.floor(Number(cap) || 0) - toSafePoints(earnedToday), 0);
}

/** Tope alcanzado (no se ganan más puntos hoy en esa dimensión). */
export function isDailyCapReached(earnedToday: unknown, cap: unknown): boolean {
  return remainingDailyCap(earnedToday, cap) === 0;
}

/** Porcentaje 0–100 para barras de progreso. */
export function progressPercent(earned: unknown, cap: unknown): number {
  const safeCap = Number(cap);
  if (!Number.isFinite(safeCap) || safeCap <= 0) {
    return 0;
  }
  return clamp(Math.round((toSafePoints(earned) / safeCap) * 100), 0, 100);
}

/** Saldo de la bolsa semanal de Karma que aún se puede regalar. */
export function remainingKarmaAllowance(givenThisWeek: unknown, weeklyAllowance: unknown): number {
  const allowance = Math.max(Math.floor(Number(weeklyAllowance) || 0), 0);
  const given = toSafePoints(givenThisWeek);
  return Math.max(allowance - given, 0);
}

/** Descripción humana de una dimensión, útil para el historial. */
export function describeDimension(dimension: PointDimension): DimensionMeta {
  return DIMENSIONS[dimension] ?? DIMENSIONS.vitalis;
}
